import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

const Register = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    setIsFormValid(name.trim() !== '' && email.trim() !== '' && mobile.length === 10);
  }, [name, email, mobile]);

  const handleRegister = async () => {
    if (!isFormValid) {
      alert('Please fill in all fields correctly.');
      return;
    }

    setLoading(true);
    try {
      // Using FormData instead of JSON
      const formData = new FormData();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('mobile', mobile);

      const response = await fetch('https://wealthmcx.com/Rest5/web_register_api', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      const result = await response.json();
      console.log(result);
      
      if (result.status === 'success' && result.otp) {
        alert('OTP sent successfully.');
        // Navigate to OTP page with state
        navigate('/otp-verification', { 
          state: { 
            uotp: result.otp, 
            name: name, 
            email: email, 
            mobile: mobile 
          } 
        });
      } else {
        alert(result.message || 'Registration failed.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Failed to connect to the server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-white">
      {/* Loading Overlay */}
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white p-4 rounded-4 text-center shadow-lg">
            <Spinner animation="border" variant="primary" style={{ width: 40, height: 40 }} />
            <div className="mt-3 text-secondary fw-semibold">Creating account...</div>
          </div>
        </div>
      )}

      {/* Top Section */}
      <div className="position-relative" style={{ 
        backgroundColor: '#f8f9fa', 
        borderBottomLeftRadius: 24, 
        borderBottomRightRadius: 24,
        minHeight: '46vh',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div className="position-absolute rounded-circle opacity-10" style={{ 
          top: '-12vh', 
          left: '-15vw', 
          width: '70vw', 
          height: '70vw', 
          backgroundColor: '#3B82F6' 
        }} />
        <div className="position-absolute rounded-circle opacity-8" style={{ 
          bottom: '-8vh', 
          right: '-8vw', 
          width: '50vw', 
          height: '50vw', 
          backgroundColor: '#3B82F6' 
        }} />
        
        {/* Image and welcome content */}
        <div className="d-flex flex-column justify-content-center align-items-center h-100 px-3 py-4">
          <img  width="200px"
                  src="https://wealthmcx.com/assets/img/logo/logo.png" 
                  alt="Wealth MCX Security" 
                  className="img-fluid"
                />
          <div className="text-center px-3">
            <h2 className="fw-bold mb-2" style={{ color: '#3B82F6', fontSize: 'clamp(18px, 5vw, 22px)' }}>
              Create Your Account
            </h2>
            <p className="small text-secondary" style={{ fontSize: 'clamp(11px, 4vw, 13px)', lineHeight: 1.4 }}>
              Join us with your details. We'll send an OTP for verification.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="px-3 pt-3 pb-4">
        {/* Name Input */}
        <div className="mb-3">
          <label className="form-label fw-semibold small mb-1" style={{ color: '#64748B', fontSize: 11 }}>
            FULL NAME
          </label>
          <div className="d-flex align-items-center bg-light rounded-3 px-3 border" style={{ 
            backgroundColor: '#F8FAFC',
            borderColor: name ? '#3B82F6' : '#E2E8F0',
            height: 46
          }}>
            <i className="bi bi-person me-2" style={{ fontSize: 16, color: '#64748B' }}></i>
            <input
              type="text"
              className="form-control bg-transparent border-0 p-0"
              style={{ fontSize: 13, color: '#0F172A' }}
              placeholder="Enter your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {name && <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: 16 }}></i>}
          </div>
        </div>

        {/* Email Input */}
        <div className="mb-3">
          <label className="form-label fw-semibold small mb-1" style={{ color: '#64748B', fontSize: 11 }}>
            EMAIL ADDRESS
          </label>
          <div className="d-flex align-items-center bg-light rounded-3 px-3 border" style={{ 
            backgroundColor: '#F8FAFC',
            borderColor: email ? '#3B82F6' : '#E2E8F0',
            height: 46
          }}>
            <i className="bi bi-envelope me-2" style={{ fontSize: 16, color: '#64748B' }}></i>
            <input
              type="email"
              className="form-control bg-transparent border-0 p-0"
              style={{ fontSize: 13, color: '#0F172A' }}
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {email && <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: 16 }}></i>}
          </div>
        </div>

        {/* Mobile Input */}
        <div className="mb-4">
          <label className="form-label fw-semibold small mb-1" style={{ color: '#64748B', fontSize: 11 }}>
            MOBILE NUMBER
          </label>
          <div className="d-flex align-items-center bg-light rounded-3 px-3 border" style={{ 
            backgroundColor: '#F8FAFC',
            borderColor: mobile.length === 10 ? '#3B82F6' : '#E2E8F0',
            height: 46
          }}>
            <i className="bi bi-phone me-2" style={{ fontSize: 16, color: '#64748B' }}></i>
            <div className="d-flex align-items-center flex-grow-1">
              <span className="fw-semibold me-2" style={{ color: '#1A1A1A', fontSize: 13 }}>+91</span>
              <input
                type="tel"
                className="form-control bg-transparent border-0 p-0"
                style={{ fontSize: 13, color: '#0F172A' }}
                placeholder="9876543210"
                maxLength={10}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            {mobile.length === 10 && <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: 16 }}></i>}
          </div>
        </div>

        {/* Register Button */}
        <button
          className="btn w-100 py-3 fw-bold mb-3"
          style={{
            backgroundColor: isFormValid ? '#3B82F6' : '#CBD5E1',
            color: '#FFF',
            fontSize: 13,
            borderRadius: 10,
            letterSpacing: 0.5,
            transition: 'all 0.3s ease',
            boxShadow: isFormValid ? '0 3px 12px rgba(59,130,246,0.25)' : 'none',
            cursor: isFormValid ? 'pointer' : 'not-allowed'
          }}
          onClick={handleRegister}
          disabled={!isFormValid || loading}
          onMouseEnter={(e) => {
            if (isFormValid) {
              e.target.style.backgroundColor = '#2563EB';
              e.target.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (isFormValid) {
              e.target.style.backgroundColor = '#3B82F6';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          {loading ? (
            <Spinner animation="border" size="sm" style={{ color: '#fff' }} />
          ) : (
            'REGISTER NOW'
          )}
        </button>

        {/* Login Link */}
        <div className="text-center">
          <span className="small text-secondary">Already have an account? </span>
          <button
            className="btn btn-link p-0 text-decoration-none fw-bold small"
            style={{ color: '#3B82F6' }}
            onClick={() => navigate('/login')}
          >
            Sign In
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .form-control:focus {
          box-shadow: none;
          outline: none;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: none;
        }
        
        .opacity-8 {
          opacity: 0.08;
        }
        
        .opacity-10 {
          opacity: 0.1;
        }
      `}</style>
    </div>
  );
};

export default Register;